/**
 * /student/exam/:attemptId/result — the student's own result and performance.
 *
 * Read-only and ownership-gated on the server. Four things, in the order that
 * helps them improve: the headline score, WHERE the marks went (by question
 * type — the MCQ/MSQ/NAT split is usually the sharpest signal), which subjects
 * and topics to work on next, and finally a per-question review with the
 * worked solution — but only when the exam permits showing the key
 * (`reveal_answers`).
 */
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Lightbulb,
  Sparkles,
  XCircle,
  BookOpen,
  PlayCircle,
} from "lucide-react";
import type {
  AnswerResponse,
  CorrectAnswer,
  QuestionOption,
  ResultByTopic,
  ResultByType,
  ResultDetailQuestion,
  ResultDetailResponse,
} from "@exam/shared";
import { AppShell } from "@/components/AppShell";
import { Badge, Card, CardBody, CardHeader } from "@/components/ui";
import { cn } from "@/lib/cn";
import { MarkdownInline, QuestionBody } from "@/lib/markdown";
import { fetchResultDetail, SAMPLE_RESULT } from "./data/result";

const TYPE_NAME: Record<string, string> = {
  MCQ: "Single-correct (MCQ)",
  MSQ: "Multi-correct (MSQ)",
  NAT: "Numerical (NAT)",
};

export function StudentResultPage() {
  const { attemptId = "" } = useParams();
  const [params] = useSearchParams();
  // Explicit layout-preview escape hatch — a real student never adds ?sample=1.
  const preview = params.get("sample") === "1";
  const [data, setData] = useState<ResultDetailResponse | null>(
    preview ? SAMPLE_RESULT : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!preview);

  useEffect(() => {
    if (preview) return;
    let cancelled = false;
    setLoading(true);
    fetchResultDetail(attemptId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load your result.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attemptId, preview]);

  return (
    <AppShell title="Your result">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <Link
          to="/student"
          className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="size-3.5" /> My exams
        </Link>

        {loading ? (
          <Card>
            <CardBody>
              <p className="text-xs text-ink-faint">Loading your result…</p>
            </CardBody>
          </Card>
        ) : error ? (
          <Card>
            <CardBody>
              <p className="text-xs text-violation">{error}</p>
              <p className="mt-1 text-2xs text-ink-faint">
                If you have just submitted, your result may take a moment to
                appear.
              </p>
            </CardBody>
          </Card>
        ) : data ? (
          <Result data={data} preview={preview} />
        ) : null}
      </div>
    </AppShell>
  );
}

function Result({
  data,
  preview,
}: {
  data: ResultDetailResponse;
  preview: boolean;
}) {
  return (
    <>
      {preview ? (
        <div className="rounded-md border border-review/40 bg-review-soft px-3 py-2 text-2xs text-review">
          Sample layout preview (?sample=1) — not real result data.
        </div>
      ) : null}

      <ScoreHeader
        score={data.overall.score}
        max={data.overall.max_score}
        percent={data.overall.percent}
        correct={data.overall.correct}
        incorrect={data.overall.incorrect}
        unanswered={data.overall.unanswered}
      />

      <TypeBreakdown types={data.by_type} />

      <SubjectBreakdown subjects={data.by_subject} />

      <WeakTopics topics={data.by_topic} />

      <QuestionReview
        questions={data.questions}
        reveal={data.reveal_answers}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Score                                                               */
/* ------------------------------------------------------------------ */

function ScoreHeader({
  score,
  max,
  percent,
  correct,
  incorrect,
  unanswered,
}: {
  score: number;
  max: number;
  percent: number;
  correct: number;
  incorrect: number;
  unanswered: number;
}) {
  return (
    <Card>
      <CardBody className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-2xs uppercase tracking-wide text-ink-faint">
            Your score
          </p>
          <p className="mt-0.5 flex items-baseline gap-2">
            <span className="tabular text-3xl font-semibold text-ink">
              {fmt(score)}
            </span>
            <span className="tabular text-lg text-ink-faint">/ {fmt(max)}</span>
            <span className="tabular ml-1 rounded-sm bg-accent-soft px-1.5 py-0.5 text-sm font-medium text-accent">
              {Math.round(percent)}%
            </span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Stat
            icon={<CheckCircle2 className="size-3.5 text-answered" />}
            label="Correct"
            value={correct}
          />
          <Stat
            icon={<XCircle className="size-3.5 text-violation" />}
            label="Incorrect"
            value={incorrect}
          />
          <Stat
            icon={<Circle className="size-3.5 text-ink-faint" />}
            label="Skipped"
            value={unanswered}
          />
        </div>
      </CardBody>
    </Card>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-3 py-1.5">
      {icon}
      <span className="text-2xs text-ink-muted">{label}</span>
      <span className="tabular text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* By type — the sharpest signal, so it sits high and reads big        */
/* ------------------------------------------------------------------ */

function TypeBreakdown({ types }: { types: ResultByType[] }) {
  if (types.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title="How you did by question type"
        description="Single-correct, multi-correct and numerical questions reward very different habits — this is usually where the biggest gains hide."
      />
      <CardBody className="grid gap-3 sm:grid-cols-3">
        {types.map((t) => {
          const acc = t.answered > 0 ? t.correct / t.answered : null;
          return (
            <div
              key={t.type}
              className="rounded-md border border-border bg-surface p-3"
            >
              <p className="text-xs font-medium text-ink">
                {TYPE_NAME[t.type] ?? t.type}
              </p>
              <p className="mt-1 flex items-baseline gap-1.5">
                <span
                  className={cn(
                    "tabular text-2xl font-semibold",
                    accuracyText(acc),
                  )}
                >
                  {acc === null ? "—" : `${Math.round(acc * 100)}%`}
                </span>
                <span className="tabular text-2xs text-ink-faint">
                  {t.correct}/{t.answered} correct
                </span>
              </p>
              <AccuracyBar accuracy={acc} />
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* By subject                                                          */
/* ------------------------------------------------------------------ */

function SubjectBreakdown({
  subjects,
}: {
  subjects: Array<{
    subject_id: string;
    subject_name: string;
    correct: number;
    answered: number;
    marks_awarded: number;
    max_marks: number;
  }>;
}) {
  if (subjects.length === 0) return null;
  return (
    <Card>
      <CardHeader title="By subject" />
      <CardBody className="flex flex-col gap-3">
        {subjects.map((s) => {
          const ratio = s.max_marks > 0 ? s.marks_awarded / s.max_marks : 0;
          return (
            <div key={s.subject_id}>
              <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
                <span className="font-medium text-ink">{s.subject_name}</span>
                <span className="tabular text-ink-muted">
                  {fmt(s.marks_awarded)} / {fmt(s.max_marks)} marks
                  <span className="ml-1.5 text-ink-faint">
                    {s.correct}/{s.answered} correct
                  </span>
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-xs bg-surface-sunken">
                <div
                  className="h-full rounded-xs bg-accent"
                  style={{ width: `${Math.max(0, Math.min(1, ratio)) * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Weak topics — framed as the next thing to work on                   */
/* ------------------------------------------------------------------ */

function WeakTopics({ topics }: { topics: ResultByTopic[] }) {
  // Topics they attempted but scored below 60% on — a genuine revision
  // list, weakest first. A topic mostly got right is not "to improve".
  const weak = topics
    .filter((t) => t.answered > 0)
    .map((t) => ({ ...t, accuracy: t.correct / t.answered }))
    .filter((t) => t.accuracy < 0.6)
    .sort((a, b) => a.accuracy - b.accuracy)
    .slice(0, 6);

  if (topics.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Topics to improve"
        description="A focused list to revise before the next attempt — start at the top."
      />
      {weak.length === 0 ? (
        <CardBody className="flex items-center gap-2">
          <Sparkles className="size-4 text-answered" />
          <p className="text-xs text-ink-muted">
            Nothing stands out as weak — you were steady across every topic you
            attempted. Keep it up.
          </p>
        </CardBody>
      ) : (
        <CardBody className="flex flex-col gap-2.5">
          {weak.map((t) => (
            <div
              key={t.topic_id}
              className="flex items-center gap-3 rounded-md border border-border bg-surface-sunken px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-ink">
                  {t.topic_name}
                </p>
                <p className="text-2xs text-ink-faint">
                  {t.subject_name}
                  {t.week_no !== null ? ` · Week ${t.week_no}` : ""}
                </p>
              </div>
              <div className="w-28 shrink-0">
                <AccuracyBar accuracy={t.accuracy} />
              </div>
              <span className="tabular w-14 shrink-0 text-right text-2xs text-ink-muted">
                {t.correct}/{t.answered}
              </span>
            </div>
          ))}
        </CardBody>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Per-question review, grouped by subject                             */
/* ------------------------------------------------------------------ */

function QuestionReview({
  questions,
  reveal,
}: {
  questions: ResultDetailQuestion[];
  reveal: boolean;
}) {
  if (questions.length === 0) return null;

  // Group by subject, preserving paper order within each.
  const bySubject = new Map<string, ResultDetailQuestion[]>();
  for (const q of [...questions].sort((a, b) => a.order_idx - b.order_idx)) {
    const list = bySubject.get(q.subject_name) ?? [];
    list.push(q);
    bySubject.set(q.subject_name, list);
  }

  return (
    <Card>
      <CardHeader
        title="Question-by-question review"
        description={
          reveal
            ? "Your answer against the correct one, with a worked solution."
            : "Your answer and the marks you earned. The answer key is hidden for this exam."
        }
      />
      <CardBody className="flex flex-col gap-5">
        {[...bySubject.entries()].map(([subject, qs]) => (
          <section key={subject}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
              {subject}
            </h3>
            <div className="flex flex-col gap-3">
              {qs.map((q) => (
                <QuestionCard key={q.order_idx} q={q} reveal={reveal} />
              ))}
            </div>
          </section>
        ))}
      </CardBody>
    </Card>
  );
}

function QuestionCard({
  q,
  reveal,
}: {
  q: ResultDetailQuestion;
  reveal: boolean;
}) {
  const chosenKeys = responseKeys(q.response);
  const correct = reveal ? correctKeys(q.correct_answer) : [];
  const showKey = reveal && q.correct_answer !== undefined;

  return (
    <article className="rounded-md border border-border bg-surface">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <span className="tabular text-2xs font-medium text-ink-faint">
          Q{q.order_idx + 1}
        </span>
        <Badge tone="neutral">{q.type}</Badge>
        <span className="text-2xs text-ink-faint">{q.topic_name}</span>
        <span className="ml-auto flex items-center gap-2">
          {reveal && q.is_correct !== null && q.is_correct !== undefined ? (
            <Badge tone={q.is_correct ? "answered" : "violation"}>
              {q.is_correct ? "Correct" : "Incorrect"}
            </Badge>
          ) : null}
          <span className="tabular text-2xs text-ink-muted">
            {q.marks_awarded === null ? "—" : fmt(q.marks_awarded)} / {fmt(q.marks)}
          </span>
        </span>
      </div>

      <div className="px-3 py-3">
        <QuestionBody source={q.body_md} />

        {q.response?.type === "CODE" ? (
          <CodeResult response={q.response} />
        ) : q.options.length > 0 ? (
          <ul className="mt-3 flex flex-col gap-1.5">
            {q.options.map((opt) => (
              <OptionRow
                key={opt.key}
                option={opt}
                chosen={chosenKeys.includes(opt.key)}
                correct={correct.includes(opt.key)}
                reveal={showKey}
              />
            ))}
          </ul>
        ) : (
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs">
            <span className="text-ink-muted">
              Your answer:{" "}
              <span className="tabular font-medium text-ink">
                {natText(q.response)}
              </span>
            </span>
            {showKey ? (
              <span className="text-ink-muted">
                Correct:{" "}
                <span className="tabular font-medium text-answered">
                  {natCorrect(q.correct_answer)}
                </span>
              </span>
            ) : null}
          </div>
        )}

        {chosenKeys.length === 0 && natText(q.response) === "—" ? (
          <p className="mt-2 text-2xs text-ink-faint">You skipped this one.</p>
        ) : null}

        {/*
          Where to go and learn it. Shown for a question they got WRONG or
          skipped — a link under a question they answered correctly is noise,
          and the page is long enough already.

          Deliberately after the solution: read why first, then go and watch.
          Reversed, the video becomes the thing you click instead of reading.
        */}
        {q.is_correct === false || q.marks_awarded === 0 ? (
          <LearnMore video={q.video_url} reading={q.reading_url} />
        ) : null}

        {showKey && q.solution_md ? (
          <div className="mt-3 rounded-md border border-border bg-surface-sunken px-3 py-2">
            <p className="mb-1 flex items-center gap-1.5 text-2xs font-medium text-ink-muted">
              <Lightbulb className="size-3.5 text-review" /> Solution
            </p>
            <QuestionBody source={q.solution_md} />
          </div>
        ) : null}
      </div>
    </article>
  );
}

/**
 * The "go and learn this" links under a wrong answer.
 *
 * Opened in a new tab so a student reading their result does not lose the
 * page, and with `noopener` because these are URLs somebody pasted into an
 * admin form — the target should never get a handle on this window.
 */
/**
 * A code answer, on the result page.
 *
 * The submitted program AND how it did, because either alone is unhelpful: the
 * counts without the source say "you failed" with nothing to look at, and the
 * source without the counts makes the student re-run it in their head to find
 * out what went wrong.
 */
function CodeResult({
  response,
}: {
  response: Extract<AnswerResponse, { type: "CODE" }>;
}) {
  return (
    <div className="mt-3 space-y-2">
      <p className="text-2xs text-ink-muted">
        {response.total === 0 ? (
          "This one had no automatic checks — it is marked by hand."
        ) : (
          <>
            <span
              className={cn(
                "tabular font-medium",
                response.passed >= response.total
                  ? "text-answered"
                  : "text-violation",
              )}
            >
              {response.passed}
            </span>{" "}
            of <span className="tabular">{response.total}</span> tests passed
          </>
        )}
      </p>

      {response.results.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {response.results.map((r) => (
            <li
              key={r.name}
              className={cn(
                "flex items-start gap-1.5 rounded-md border px-2 py-1 text-2xs",
                r.passed
                  ? "border-answered/30 bg-answered-soft"
                  : "border-violation/30 bg-violation-soft",
              )}
            >
              {r.passed ? (
                <CheckCircle2 className="mt-px size-3 shrink-0 text-answered" />
              ) : (
                <XCircle className="mt-px size-3 shrink-0 text-violation" />
              )}
              <span className="min-w-0">
                <span className="block text-ink">{r.name}</span>
                {r.detail ? (
                  <span className="block font-mono text-ink-muted">
                    {r.detail}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <details className="rounded-md border border-border bg-surface-sunken">
        <summary className="cursor-pointer px-2.5 py-1.5 text-2xs font-medium text-ink-muted">
          Your code
        </summary>
        <pre className="overflow-x-auto border-t border-border px-2.5 py-2 font-mono text-2xs text-ink">
          {response.source}
        </pre>
      </details>
    </div>
  );
}

function LearnMore({
  video,
  reading,
}: {
  video?: string | null;
  reading?: string | null;
}) {
  if (!video && !reading) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-accent/30 bg-accent-soft px-3 py-2">
      <span className="text-2xs font-medium text-accent">Learn this</span>
      {video ? (
        <a
          href={video}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 rounded-sm border border-accent/40 bg-surface px-2 py-1 text-2xs font-medium text-accent hover:bg-accent-soft"
        >
          <PlayCircle className="size-3.5" /> Watch
        </a>
      ) : null}
      {reading ? (
        <a
          href={reading}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 rounded-sm border border-accent/40 bg-surface px-2 py-1 text-2xs font-medium text-accent hover:bg-accent-soft"
        >
          <BookOpen className="size-3.5" /> Read
        </a>
      ) : null}
    </div>
  );
}

function OptionRow({
  option,
  chosen,
  correct,
  reveal,
}: {
  option: QuestionOption;
  chosen: boolean;
  correct: boolean;
  reveal: boolean;
}) {
  return (
    <li
      className={cn(
        "flex items-start gap-2 rounded-sm border px-2 py-1.5 text-xs",
        reveal && correct
          ? "border-answered/40 bg-answered-soft"
          : reveal && chosen && !correct
            ? "border-violation/40 bg-violation-soft"
            : chosen
              ? "border-accent/40 bg-accent-soft"
              : "border-border bg-surface",
      )}
    >
      <span className="tabular mt-px w-4 shrink-0 font-medium text-ink-faint">
        {option.key}
      </span>
      <span className="min-w-0 flex-1 text-ink">
        <MarkdownInline source={option.text} />
      </span>
      <span className="ml-auto flex shrink-0 items-center gap-1.5 text-2xs">
        {chosen ? (
          <span className="text-ink-muted">Your answer</span>
        ) : null}
        {reveal && correct ? (
          <CheckCircle2 className="size-3.5 text-answered" />
        ) : reveal && chosen && !correct ? (
          <XCircle className="size-3.5 text-violation" />
        ) : null}
      </span>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

function AccuracyBar({ accuracy }: { accuracy: number | null }) {
  const pct = accuracy === null ? 0 : Math.max(0, Math.min(1, accuracy)) * 100;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-xs bg-surface-sunken">
      <div
        className={cn("h-full rounded-xs", accuracyBg(accuracy))}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Honest but not harsh: strong green, middling amber, weak red. */
function accuracyText(a: number | null): string {
  if (a === null) return "text-ink-faint";
  if (a >= 0.6) return "text-answered";
  if (a >= 0.35) return "text-review";
  return "text-violation";
}

function accuracyBg(a: number | null): string {
  if (a === null) return "bg-border-strong";
  if (a >= 0.6) return "bg-answered";
  if (a >= 0.35) return "bg-review";
  return "bg-violation";
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

function responseKeys(r: AnswerResponse | null): string[] {
  if (!r) return [];
  if (r.type === "MCQ") return r.key ? [r.key] : [];
  if (r.type === "MSQ") return r.keys;
  return [];
}

function correctKeys(c: CorrectAnswer | undefined): string[] {
  if (!c) return [];
  if (c.type === "MCQ") return [c.key];
  if (c.type === "MSQ") return c.keys;
  return [];
}

function natText(r: AnswerResponse | null): string {
  if (r && r.type === "NAT" && r.value !== null) return fmt(r.value);
  return "—";
}

function natCorrect(c: CorrectAnswer | undefined): string {
  if (c && c.type === "NAT") return fmt(c.value);
  return "—";
}
